import { Dialogs, Toast } from '@leverege/ui-elements';
import { GlobalState } from '@leverege/ui-redux';
import { Context } from '@leverege/plugin';
import { Attributes, DataSources } from '@leverege/ui-attributes';
import I18N from '@leverege/i18n';
import { Types } from "../../constants/index.js";
import FirmwareReleaseActionDialog from "../../views/firmwareReleaseAction/FirmwareReleaseActionDialog.js";
const {
  tIcon
} = I18N.ns('api-ui-fota-management.Imagine');
export default function StopFirmwareRelease({
  deviceType,
  blueprint
}) {
  return {
    id: `blueprint.action.fota.${blueprint.type}.${deviceType.id}.stopFirmwareRelease`,
    name: 'Stop Firmware Release',
    layout: {
      sort: `zz.fota.firmwareRelease.${deviceType.id}.stop`,
      group: ['fota', deviceType.id],
      groupNames: ['Firmware', Attributes.get('name', Types.deviceType, deviceType)],
      groupIcons: [tIcon('GroupIcons.firmware'), tIcon('GroupIcons.deviceType')]
    },
    matches: [{
      use: ['contextMenu', 'mobileActionBar'],
      objectType: blueprint.type
    }, {
      client: ['MapBoxRolloverSelectionPointer', 'ListDataViewer'],
      use: ['contextMenu'],
      objectType: blueprint.type
    }],
    isPrepared: cxt => {
      const {
        clientProps: {
          relationship
        }
      } = cxt;
      const target = Context.getTargetOfType(cxt, relationship.objectType);
      if (!target) {
        // nothing to do
        return true;
      }
      const releaseActions = DataSources.getActions({
        type: Types.firmwareRelease,
        ref: '/firmwareRelease'
      });
      const groupActions = DataSources.getActions({
        type: Types.deviceGroup,
        ref: '/deviceGroup'
      });
      const canList = groupActions.canList();
      const list = groupActions.getList(GlobalState.get(), {
        queryName: `${deviceType.id}/${target.id}`
      });
      const releaseId = Attributes.get('firmwareReleaseId', Types.deviceGroup, list?.items?.[0]);
      const canGetRelease = releaseActions.canGet(releaseId);
      const release = releaseActions.getItem(releaseId);
      const canStop = releaseActions.canStop(releaseId);
      const deviceTypeId = list?.extra?.query?.deviceTypeId;
      const imagineGroupId = list?.extra?.query?.imagineGroupId;

      // if we don't have permissions to list firmware, we can't do this, so we are ready
      if (canList === false) {
        return true;
      }

      // if we haven't checked if we can list we aren't ready
      if (canList == null) {
        return false;
      }

      // if we can list, but don't have the list, we aren't ready
      if (!list || deviceTypeId !== deviceType.id || imagineGroupId !== target.id) {
        return false;
      }

      // something is wrong, nothing to do
      if (list?.items?.length !== 1) {
        return true;
      }

      // if we have the list and its the right length, but we don't have the release object
      // we aren't ready
      if (releaseId && canGetRelease == null) {
        return false;
      }
      if (canGetRelease && release == null) {
        return false;
      }
      if (release && canStop == null) {
        return false;
      }
      return true;
    },
    prepare: async cxt => {
      const {
        clientProps: {
          relationship
        }
      } = cxt;
      const target = Context.getTargetOfType(cxt, relationship.objectType);
      const releaseActions = DataSources.getActions({
        type: Types.firmwareRelease,
        ref: '/firmwareRelease'
      });
      const groupActions = DataSources.getActions({
        type: Types.deviceGroup,
        ref: '/deviceGroup'
      });
      const canList = await GlobalState.dispatch(groupActions.requestCanList());
      let list;
      if (canList) {
        list = await GlobalState.dispatch(groupActions.list({
          deviceTypeId: deviceType.id,
          imagineGroupId: target.id
        }, {
          queryName: `${deviceType.id}/${target.id}`
        }));
      }
      if (list?.items?.length === 1) {
        const releaseId = Attributes.get('firmwareReleaseId', Types.deviceGroup, list?.items?.[0]);
        if (releaseId) {
          const canGet = await GlobalState.dispatch(releaseActions.requestCanGet(releaseId));
          if (canGet) {
            await GlobalState.dispatch(releaseActions.get(releaseId));
            await GlobalState.dispatch(releaseActions.requestCanStop(releaseId));
          }
        }
      }
    },
    handles: cxt => {
      const {
        clientProps: {
          relationship
        }
      } = cxt;
      const target = Context.getTargetOfType(cxt, relationship.objectType);
      if (!target) {
        return false;
      }
      const releaseActions = DataSources.getActions({
        type: Types.firmwareRelease,
        ref: '/firmwareRelease'
      });
      const groupActions = DataSources.getActions({
        type: Types.deviceGroup,
        ref: '/deviceGroup'
      });
      const list = groupActions.getList(GlobalState.get(), {
        queryName: `${deviceType.id}/${target.id}`
      });
      const releaseId = Attributes.get('firmwareReleaseId', Types.deviceGroup, list?.items?.[0]);
      const canStop = releaseActions.canStop(releaseId);
      const release = releaseActions.getItem(releaseId);
      return canStop && Attributes.get('state', Types.firmwareRelease, release) !== 'stopped';
    },
    appearance: ({
      context
    }) => {
      const target = Context.getTargetOfType(context, blueprint.type);
      return {
        name: `Stop Firmware Release at ${Attributes.get('name', blueprint.type, target)}`,
        icon: tIcon('StopAction.icon')
      };
    },
    perform: ({
      context
    }) => {
      const {
        reloadData
      } = context;
      const groupActions = DataSources.getActions({
        type: Types.deviceGroup,
        ref: '/deviceGroup'
      });
      const target = Context.getTargetOfType(context, blueprint.type);
      const list = groupActions.getList(GlobalState.get(), {
        queryName: `${deviceType.id}/${target.id}`
      });
      const releaseId = Attributes.get('firmwareReleaseId', Types.deviceGroup, list?.items?.[0]);
      const releaseActions = DataSources.getActions({
        type: Types.firmwareRelease,
        ref: '/firmwareRelease',
        id: releaseId
      });
      Dialogs.show({
        component: FirmwareReleaseActionDialog,
        props: {
          onSubmit: async () => {
            try {
              await GlobalState.dispatch(releaseActions.stop());
              Toast.success('Successfully stopd firmware release.');
            } catch (err) {
              Toast.error(['Failed to stop firmware release.', err.message]);
            }
            reloadData?.();
          },
          actionName: 'Stop',
          target: {
            type: Types.firmwareRelease,
            ref: '/firmwareRelease',
            id: releaseId,
            data: releaseActions.getItem()
          }
        }
      });
    }
  };
}